import { z } from "zod";

/**
 * Validates that required date fields are present
 */
const validateRequiredDateFields = (month: string, day: string, year: string, ctx: z.RefinementCtx): boolean => {
    if (!month) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please select a month",
            path: ["month"],
        });
        return false;
    }
    if (!day) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter a valid day",
            path: ["day"],
        });
        return false;
    }
    if (!year) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter a valid year",
            path: ["year"],
        });
        return false;
    }
    return true;
};

/**
 * Validates date component ranges
 */
const validateDateRanges = (monthNum: number, dayNum: number, yearNum: number, ctx: z.RefinementCtx): boolean => {
    if (monthNum < 1 || monthNum > 12) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter a valid month",
            path: ["month"],
        });
        return false;
    }
    if (dayNum < 1 || dayNum > 31) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter a valid day",
            path: ["day"],
        });
        return false;
    }
    if (yearNum < 1900 || yearNum > new Date().getFullYear()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter a valid year",
            path: ["year"],
        });
        return false;
    }
    return true;
};

/**
 * Validates that the date is a real calendar date
 */
const validateDateValidity = (yearNum: number, monthNum: number, dayNum: number, ctx: z.RefinementCtx): boolean => {
    const date = new Date(yearNum, monthNum - 1, dayNum);
    const isValidDate = date.getFullYear() === yearNum && date.getMonth() === monthNum - 1 && date.getDate() === dayNum;

    if (!isValidDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter a valid date",
            path: ["day"],
        });
        return false;
    }
    return true;
};

/**
 * Calculates age from date of birth
 */
const calculateAge = (yearNum: number, monthNum: number, dayNum: number): number => {
    const today = new Date();
    return (
        today.getFullYear() -
        yearNum -
        (today.getMonth() < monthNum - 1 || (today.getMonth() === monthNum - 1 && today.getDate() < dayNum) ? 1 : 0)
    );
};

/**
 * Validates age constraints
 */
const validateAgeConstraints = (age: number, maxAge: number, ctx: z.RefinementCtx): boolean => {
    if (age < 18) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "You must be 18 or older to apply",
            path: ["year"],
        });
        return false;
    }
    if (age >= maxAge) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `You must be under ${maxAge} years old to apply`,
            path: ["year"],
        });
        return false;
    }
    if (age > 120) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter a valid date of birth",
            path: ["year"],
        });
        return false;
    }
    return true;
};

/**
 * Validates that age falls within an inclusive [minAge, maxAge] range
 */
const validateAgeRange = (age: number, minAge: number, maxAge: number, ctx: z.RefinementCtx): boolean => {
    if (age < minAge || age > maxAge) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Age must be between ${minAge} and ${maxAge}`,
            path: ["year"],
        });
        return false;
    }
    return true;
};

/**
 * Builds the shared Date of Birth schema (required fields, ranges, calendar validity)
 * and defers the age rule to the caller
 */
const buildDateOfBirthSchema = (applyAgeRule: (age: number, ctx: z.RefinementCtx) => void) =>
    z
        .object({
            month: z.string().min(1, { message: "Please select a month" }),
            day: z
                .string()
                .min(1, { message: "Please enter a valid day" })
                .refine(
                    (val) => {
                        const dayNum = parseInt(val, 10);
                        return !isNaN(dayNum) && dayNum >= 1 && dayNum <= 31;
                    },
                    { message: "Please enter a valid day" }
                ),
            year: z
                .string()
                .min(1, { message: "Please enter a valid year" })
                .refine(
                    (val) => {
                        const yearNum = parseInt(val, 10);
                        return !isNaN(yearNum) && yearNum >= 1900 && yearNum <= new Date().getFullYear();
                    },
                    { message: "Please enter a valid year" }
                ),
        })
        .refine(
            (data) => {
                const { month, day } = data;
                // Check if day is entered without month
                if (day && !month) {
                    return false;
                }
                return true;
            },
            {
                message: "Please select a month",
                path: ["month"],
            }
        )
        .refine(
            (data) => {
                const { month, day } = data;
                // Check for invalid date combinations (e.g., April 31)
                if (month && day) {
                    const monthNum = parseInt(month, 10);
                    const dayNum = parseInt(day, 10);

                    if (!isNaN(monthNum) && !isNaN(dayNum)) {
                        // Days in each month (Feb has 29 for now, will check leap year later)
                        const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

                        if (monthNum >= 1 && monthNum <= 12) {
                            if (dayNum > daysInMonth[monthNum - 1]) {
                                return false;
                            }
                        }
                    }
                }
                return true;
            },
            {
                message: "Please enter a valid date",
                path: ["day"],
            }
        )
        .superRefine((data, ctx) => {
            const { month, day, year } = data;

            if (!validateRequiredDateFields(month, day, year, ctx)) return;

            const monthNum = parseInt(month, 10);
            const dayNum = parseInt(day, 10);
            const yearNum = parseInt(year, 10);

            if (!validateDateRanges(monthNum, dayNum, yearNum, ctx)) return;
            if (!validateDateValidity(yearNum, monthNum, dayNum, ctx)) return;

            const age = calculateAge(yearNum, monthNum, dayNum);
            applyAgeRule(age, ctx);
        });

/**
 * Creates a Date of Birth Schema with comprehensive validation
 * @param maxAge - Maximum age allowed (default: 75, loyalty: 100)
 */
export const createDateOfBirthSchema = (maxAge: number = 75) =>
    buildDateOfBirthSchema((age, ctx) => validateAgeConstraints(age, maxAge, ctx));

/**
 * Creates a Date of Birth Schema that constrains age to an inclusive [minAge, maxAge] range
 */
export const createDateOfBirthRangeSchema = (minAge: number, maxAge: number) =>
    buildDateOfBirthSchema((age, ctx) => validateAgeRange(age, minAge, maxAge, ctx));

/** DTO date-of-birth schema: applicants must be 18 and under 75 */
export const dtoDateOfBirthSchema = createDateOfBirthSchema(75);

/** MLTA date-of-birth schema: applicants must be 18 and under 100 */
export const mltaDateOfBirthSchema = createDateOfBirthSchema(100);

/** GIWL date-of-birth schema: applicants must be between 45 and 85 (inclusive) */
export const giwlDateOfBirthSchema = createDateOfBirthRangeSchema(45, 85);

// Date of Birth Schema with comprehensive validation (default maxAge: 75)
export const dateOfBirthSchema = createDateOfBirthSchema(75);

export const optionalDateOfBirthSchema = z
    .object({
        month: z.string().optional(),
        day: z.string().optional(),
        year: z.string().optional(),
    })
    .optional();
